import axios, { AxiosError } from "axios";
import { env } from "@/lib/env";
import { isMock } from "@/lib/mock-flags";
import type { AdminUser } from "../stores/authStore";

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: AdminUser;
}

export type AuthErrorKind = "invalid" | "twoFactorRequired" | "locked" | "network";

export class AuthError extends Error {
  constructor(public readonly kind: AuthErrorKind) {
    super(kind);
    this.name = "AuthError";
  }
}

// Cliente "cru" (sem interceptors) para os endpoints de auth — evita recursão com
// o interceptor de refresh e não anexa Authorization indevidamente.
// `withCredentials` envia/recebe o cookie HttpOnly `rt` (refresh token) — que o JS
// nunca lê nem persiste, mitigando exfiltração por XSS.
const authClient = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

function mapError(err: unknown): AuthError {
  if (err instanceof AxiosError) {
    if (err.response?.status === 401) {
      // O backend devolve um `code` estável quando disponível (ProblemDetails).
      const code = (err.response.data as { code?: string } | undefined)?.code;
      if (code === "auth.2fa_required") return new AuthError("twoFactorRequired");
      if (code === "auth.account_locked") return new AuthError("locked");
      return new AuthError("invalid");
    }
    return new AuthError("network");
  }
  return new AuthError("network");
}

// ───────────────────────── mock ─────────────────────────

interface MockUser extends AdminUser {
  password: string;
}

const mockUsers: MockUser[] = [
  {
    id: "0190f100-0000-7000-8000-000000000001",
    name: "Super Admin",
    email: "admin@imobgo.app",
    role: "super_admin",
    twoFactorEnabled: false,
    password: "DevAdminPass123!",
  },
  {
    id: "0190f100-0000-7000-8000-000000000002",
    name: "Operador Financeiro",
    email: "financeiro@imobgo.app",
    role: "financial",
    twoFactorEnabled: true,
    password: "DevFinancePass123!",
  },
];

// Em mock, a "sessão" (equivalente ao cookie HttpOnly no modo real) vive só na
// memória deste módulo — o refresh token nunca toca em storage.
let currentMockUser: AdminUser | null = null;

function toPublic(user: MockUser): AdminUser {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role,
    twoFactorEnabled: user.twoFactorEnabled,
  };
}

function randomToken(): string {
  return crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "");
}

function sessionFor(user: AdminUser): LoginResponse {
  currentMockUser = user;
  return { accessToken: `mock.${randomToken()}`, expiresIn: 900, user };
}

async function loginMock(email: string, password: string, totpCode?: string): Promise<LoginResponse> {
  await new Promise((r) => setTimeout(r, 250));
  const user = mockUsers.find((u) => u.email.toLowerCase() === email.toLowerCase());
  if (!user || user.password !== password) throw new AuthError("invalid");
  if (user.twoFactorEnabled) {
    if (!totpCode) throw new AuthError("twoFactorRequired");
    if (!/^\d{6}$/.test(totpCode)) throw new AuthError("invalid");
  }
  return sessionFor(toPublic(user));
}

async function refreshMock(): Promise<LoginResponse> {
  if (!currentMockUser) throw new AuthError("invalid");
  return sessionFor(currentMockUser);
}

async function logoutMock(): Promise<void> {
  currentMockUser = null;
}

// ───────────────────────── real ─────────────────────────

async function loginReal(email: string, password: string, totpCode?: string): Promise<LoginResponse> {
  try {
    const { data } = await authClient.post<LoginResponse>("/auth/login", { email, password, totpCode });
    return data;
  } catch (err) {
    throw mapError(err);
  }
}

// O refresh token vai no cookie HttpOnly (enviado por `withCredentials`); sem corpo.
async function refreshReal(): Promise<LoginResponse> {
  try {
    const { data } = await authClient.post<LoginResponse>("/auth/refresh");
    return data;
  } catch (err) {
    throw mapError(err);
  }
}

async function logoutReal(accessToken: string | null): Promise<void> {
  // Cookie `rt` acompanha via withCredentials; o access token autentica a revogação.
  await authClient.post(
    "/auth/logout",
    undefined,
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  );
}

// ───────────────────────── facade ─────────────────────────

export function login(email: string, password: string, totpCode?: string): Promise<LoginResponse> {
  return isMock("auth") ? loginMock(email, password, totpCode) : loginReal(email, password, totpCode);
}

export function refresh(): Promise<LoginResponse> {
  return isMock("auth") ? refreshMock() : refreshReal();
}

export function logout(accessToken: string | null): Promise<void> {
  return isMock("auth") ? logoutMock() : logoutReal(accessToken);
}
